/**
 * Redirect-after-login service.
 *
 * Persists the intended destination URL across OAuth redirects that cross
 * domain boundaries (e.g. argel.fpm.cloud → fpm.cloud → argel.fpm.cloud).
 *
 * sessionStorage flag `fpm_sync_reload` prevents multi-tab logout from
 * overwriting the saved redirect path when a Vuex Sync message triggers
 * a reload on another tab.
 */

import { setCookie, getCookie, removeCookie } from '@/utils/cookies'

const REDIRECT_COOKIE_KEY = 'fpm_front_redirect'
const SYNC_RELOAD_FLAG = 'fpm_sync_reload'

function _rootDomain() {
  const parts = window.location.hostname.split('.')
  if (parts.length > 1) return parts.slice(-2).join('.')
  throw new Error('redirectAfterLogin: unknown host format')
}

function _isValidPath(path) {
  if (!path || typeof path !== 'string') return false
  try {
    const origin = `${window.location.protocol}//${window.location.host}`
    const url = new URL(path, origin)
    return url.origin === origin && url.pathname.startsWith('/')
  } catch (_) {
    return false
  }
}

/**
 * Persist a relative redirect path in a parent-domain cookie so it survives
 * cross-subdomain OAuth redirects.
 *
 * No-op if `path` is not a valid same-origin relative path.
 *
 * @param {string} path  e.g. "/point-icons"
 */
export function saveRedirectPath(path) {
  if (!_isValidPath(path) || consumeSyncReloadFlag()) return
  setCookie(REDIRECT_COOKIE_KEY, path, _rootDomain())
}

/**
 * Read and immediately clear the saved redirect path.
 *
 * @param {string} [defaultPath]  fallback when no valid path is stored
 * @returns {string|null}
 */
export function consumeRedirectPath(defaultPath) {
  const rootDomain = _rootDomain()
  let path = getCookie(REDIRECT_COOKIE_KEY)
  removeCookie(REDIRECT_COOKIE_KEY, rootDomain)

  if (!_isValidPath(path)) {
    path = defaultPath ?? null
  }

  return path
}

/**
 * Mark the current page load as triggered by a cross-tab Sync message.
 * Must be called *before* `window.location.reload()` so the flag survives
 * the reload (sessionStorage persists across same-origin reloads).
 */
export function markSyncReload() {
  try {
    sessionStorage.setItem(SYNC_RELOAD_FLAG, '1')
  } catch (_) {
    // ignore (private browsing, storage quota, etc.)
  }
}

/**
 * Read and clear the sync-reload flag.
 *
 * @returns {boolean}  true if the current load was triggered by a Sync message
 */
export function consumeSyncReloadFlag() {
  const flag = sessionStorage.getItem(SYNC_RELOAD_FLAG)
  try {
    sessionStorage.removeItem(SYNC_RELOAD_FLAG)
  } catch (_) {
    // ignore (private browsing, storage quota, etc.)
  }
  return Boolean(flag)
}

/**
 * Build the full URL to redirect the user to after a successful login.
 *
 * @param {string} userDomain   workspace subdomain (e.g. "argel")
 * @param {string} frontDomain  root domain (e.g. "fpm.cloud")
 * @param {string} path         relative path (e.g. "/point-icons")
 * @returns {string}
 */
export function buildWorkspaceRedirectUrl(userDomain, frontDomain, path) {
  return `https://${userDomain}.${frontDomain}${path || ''}`
}
